import React, { CSSProperties, HTMLAttributes } from 'react';
import clsx from 'clsx';
import isEqual from 'lodash/isEqual';
import Select from 'react-select';
import { createStyles, emphasize, makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import NoSsr from '@material-ui/core/NoSsr';
import TextField, { BaseTextFieldProps } from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import MenuItem, { MenuItemProps } from '@material-ui/core/MenuItem';
import CancelIcon from '@material-ui/icons/Cancel';
import { ValueContainerProps } from 'react-select/src/components/containers';
import { ControlProps } from 'react-select/src/components/Control';
import { MenuProps, NoticeProps } from 'react-select/src/components/Menu';
import { MultiValueProps } from 'react-select/src/components/MultiValue';
import { OptionProps } from 'react-select/src/components/Option';
import { PlaceholderProps } from 'react-select/src/components/Placeholder';
import { SingleValueProps } from 'react-select/src/components/SingleValue';
import { ValueType } from 'react-select/src/types';
import { Omit } from '@material-ui/types';
import { StylesConfig } from 'react-select/src/styles';
import { FormHelperText } from '@material-ui/core';

export interface OptionType {
  label: string;
  value: string | object;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      minWidth: 290
    },
    input: {
      display: 'flex',
      padding: '0.75rem',
      height: 'auto'
    },
    valueContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      flex: 1,
      alignItems: 'center',
      overflow: 'hidden'
    },
    chip: {
      margin: theme.spacing(0.5, 0.25)
    },
    chipFocused: {
      backgroundColor: emphasize(theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700], 0.08)
    },
    noOptionsMessage: {
      padding: theme.spacing(1, 2)
    },
    singleValue: {
      fontSize: 16
    },
    placeholder: {
      position: 'relative',
      fontSize: 16
    },
    paper: {
      position: 'absolute',
      zIndex: 10,
      marginTop: theme.spacing(1),
      left: 0,
      right: 0
    },
    divider: {
      height: theme.spacing(2)
    }
  })
);

function NoOptionsMessage(props: NoticeProps<OptionType, false>) {
  return (
    <Typography color="textSecondary" className={props.selectProps.classes.noOptionsMessage} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

type InputComponentProps = Pick<BaseTextFieldProps, 'inputRef'> & HTMLAttributes<HTMLDivElement>;

function inputComponent({ inputRef, ...props }: InputComponentProps) {
  return <div ref={inputRef} {...props} />;
}

function Control(props: ControlProps<OptionType, false>) {
  const {
    children,
    innerProps,
    innerRef,
    selectProps: { classes, TextFieldProps }
  } = props;

  return (
    <TextField
      fullWidth
      variant="outlined"
      InputProps={{
        inputComponent,
        inputProps: {
          className: classes.input,
          ref: innerRef,
          children,
          ...innerProps
        }
      }}
      {...TextFieldProps}
    />
  );
}

function Option(props: OptionProps<OptionType, false>) {
  return (
    <MenuItem
      {...(props.innerProps as unknown as MenuItemProps)}
      ref={props.innerRef}
      button={undefined}
      selected={props.isFocused}
      style={{
        fontWeight: props.isSelected ? 500 : 400
      }}
    >
      {props.children}
    </MenuItem>
  );
}

type MuiPlaceholderProps = Omit<PlaceholderProps<OptionType, false>, 'innerProps'> &
  Partial<Pick<PlaceholderProps<OptionType, false>, 'innerProps'>>;
function Placeholder(props: MuiPlaceholderProps) {
  const { selectProps, innerProps = {}, children } = props;
  return (
    <Typography color="textSecondary" className={selectProps.classes.placeholder} {...innerProps}>
      {children}
    </Typography>
  );
}

function SingleValue(props: SingleValueProps<OptionType>) {
  return (
    <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function ValueContainer(props: ValueContainerProps<OptionType, false>) {
  return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
}

function MultiValue(props: MultiValueProps<OptionType>) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={clsx(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused
      })}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  );
}

function Menu(props: MenuProps<OptionType, false>) {
  return (
    <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  );
}

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer
};

interface AutoSuggestProps {
  name: string;
  onChange: (value: string | object) => void;
  value: string | object | null;
  label: string;
  suggestions: OptionType[];
  placeholder?: string;
  error?: boolean;
}

const AutoSuggest = ({ name, onChange, value, label, suggestions, placeholder = '', error = false }: AutoSuggestProps) => {
  const theme = useTheme();
  const classes = useStyles(theme);

  const [single, setSingle] = React.useState<ValueType<OptionType, false>>(() => {
    return suggestions.find((suggestion) => isEqual(suggestion.value, value));
  });

  function handleChangeSingle(values: ValueType<OptionType, false>) {
    setSingle(values);
    if (values) {
      const { value } = values as OptionType;
      console.log(value);
      onChange(value);
    }
  }

  const selectStyles = {
    input: (base: CSSProperties) => ({
      ...base,
      color: theme.palette.text.primary,
      '& input': {
        font: 'inherit'
      }
    })
  };

  return (
    <div className={classes.root}>
      <NoSsr>
        <Select
          isClearable={true}
          name={name}
          classes={classes}
          styles={selectStyles as StylesConfig<OptionType, false>}
          inputId="react-select-single"
          TextFieldProps={{
            label,
            InputLabelProps: {
              htmlFor: 'react-select-single',
              shrink: true
            }
          }}
          placeholder={placeholder}
          options={suggestions}
          components={components}
          value={suggestions.find((suggestion) => isEqual(suggestion.value, value))}
          onChange={handleChangeSingle}
          variant="outlined"
        />
        {error && <FormHelperText error={error}>This field is required.</FormHelperText>}
      </NoSsr>
    </div>
  );
};

export default AutoSuggest;
